// Devis d'un dossier : liste (révisions, variantes, totaux) et création.
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen, FileSpreadsheet, Plus } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { date, euros, pluriel } from '../../lib/format.ts';
import { Alerte, Badge, Bouton, Champ, Chargement, EtatErreur, EtatVide, LienBouton, Panneau, useNotifier } from '../../components/ui.tsx';
import { BadgeStatutDevis } from '../../components/statuts.tsx';
import { DossierCadre } from '../etudes/DossierCadre.tsx';
import { PanneauDevisRecus } from './devis-recus.tsx';
import { ListeImportsPdf } from './import-pdf.tsx';

interface DevisListe {
  id: string; number: string; title: string; created_at: string; created_by_name: string | null;
  revisions: { id: string; rev_no: number; variant: string; status: string; emitted_at: string | null; updated_at: string; accepted_on?: string | null;
    resume: { complet: boolean; nb_lignes: number; nb_a_renseigner: number; ht: string | null; ttc: string | null } }[];
}

export function PageDevisDossier() {
  const { id = '' } = useParams();
  const { peut } = useSession();
  const qc = useQueryClient();
  const naviguer = useNavigate();
  const notifier = useNotifier();
  const [titre, setTitre] = useState('');
  const q = useQuery({ queryKey: ['devis-dossier', id], queryFn: () => api.get<DevisListe[]>(`/api/dossiers/${id}/devis`) });
  const creer = useMutation({
    mutationFn: (t: string) => api.post<{ quoteId: string; revisionId: string; numero: string }>(`/api/dossiers/${id}/devis`, { titre: t }),
    onSuccess: (r) => {
      notifier(`Devis ${r.numero} créé.`);
      void qc.invalidateQueries({ queryKey: ['devis-dossier', id] });
      void qc.invalidateQueries({ queryKey: ['dossier', id] });
      naviguer(`/etudes/${id}/devis/${r.quoteId}`);
    },
  });
  const soumettre = (e: FormEvent) => { e.preventDefault(); if (titre.trim()) creer.mutate(titre.trim()); };

  return (
    <DossierCadre dossierId={id} actif="devis"
      actions={<LienBouton to="/etudes/catalogue" icone={<BookOpen aria-hidden />}>Catalogue de prix</LienBouton>}>
      {peut('devis.ecrire') && (
        <Panneau titre="Nouveau devis">
          <form className="devis-creation devis-page" onSubmit={soumettre}>
            <Champ libelle="Titre du devis" placeholder="Ex. Remplacement des tableaux et comptage" value={titre} maxLength={300}
              onChange={(e) => setTitre(e.target.value)} erreur={creer.isError ? messageErreur(creer.error) : null} required />
            <Bouton type="submit" variante="principal" icone={<Plus aria-hidden />} chargement={creer.isPending} disabled={!titre.trim()}>
              Créer le devis
            </Bouton>
          </form>
          <p className="petit discret" style={{ marginTop: 8 }}>
            Le devis reçoit un numéro DEV-AAAA-NNNN et une révision 1 « Base » en brouillon. Les prix inconnus restent « À renseigner ».
          </p>
        </Panneau>
      )}
      {q.isLoading && <Chargement />}
      {q.isError && <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />}
      {q.data && q.data.length === 0 && (
        <Panneau>
          <EtatVide icone={<FileSpreadsheet />} titre="Aucun devis pour ce dossier"
            texte={peut('devis.ecrire') ? 'Créez le premier devis : vous pourrez y reprendre les exigences non chiffrées du dossier et les prix du catalogue.'
              : 'Aucun devis n’a encore été préparé pour ce dossier.'} />
        </Panneau>
      )}
      {q.data && q.data.length > 0 && (
        <Panneau serre titre={pluriel(q.data.length, 'devis', 'devis')}>
          <div className="liste">
            {q.data.map((d) => (
              <div key={d.id} className="devis-carte-liste">
                <div className="rangee entre">
                  <Link to={`/etudes/${id}/devis/${d.id}`} className="devis-lien-titre">
                    <span className="mono">{d.number}</span> <span>{d.title}</span>
                  </Link>
                  <span className="tres-petit discret">Créé le {date(d.created_at)}{d.created_by_name ? ` par ${d.created_by_name}` : ''}</span>
                </div>
                <ul className="devis-revisions-liste">
                  {d.revisions.map((r) => (
                    <li key={r.id}>
                      <Link to={`/etudes/${id}/devis/${d.id}?revision=${r.id}`} className="devis-revision-lien">
                        <span>{r.variant} — révision {r.rev_no}</span>
                        <BadgeStatutDevis statut={r.status} />
                        {r.resume.complet
                          ? <span className="chiffre petit">{r.resume.nb_lignes ? `${euros(r.resume.ht)} HT` : 'Aucune ligne'}</span>
                          : <Badge ton="ambre">{`Total incomplet : ${pluriel(r.resume.nb_a_renseigner, 'ligne')} à renseigner`}</Badge>}
                        {r.accepted_on && <Badge ton="succes">{`Acceptée le ${date(r.accepted_on)}`}</Badge>}
                        {r.emitted_at && <span className="tres-petit discret masque-etroit">Émis le {date(r.emitted_at)}</span>}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Panneau>
      )}
      <PanneauDevisRecus dossierId={id} />
      <ListeImportsPdf dossierId={id} />
      {!peut('devis.couts') && q.data && q.data.length > 0 && (
        <Alerte titre="Coûts et marges réservés">Votre rôle affiche les prix de vente du devis ; les coûts, taux et marges sont réservés aux chargés d’affaires et référents.</Alerte>
      )}
    </DossierCadre>
  );
}
